import { z } from "zod";
import {
  actionsSchema,
  keystageSlugs,
  pathwayDescriptions,
  pathwaySlugs,
  phaseSlugs,
  SyntheticUnitvariantsWithLessonIdsByKsCamel,
  syntheticUnitvariantsWithLessonIdsByKsSchema,
  tierDescriptions,
  tierSlugs,
  years,
} from "@oaknational/oak-curriculum-schema";
import zodToCamelCase from "zod-to-camel-case";

import { mediaClipsRecordCamelSchema } from "./queries/lessonMediaClips/lessonMediaClips.schema";

export const contentGuidanceSchemaCamelCase = z.object({
  contentGuidanceLabel: z.string(),
  contentGuidanceDescription: z.string(),
  contentGuidanceArea: z.string(),
});

export const misconceptionsAndCommonMistakesSchema = z.object({
  misconception: z.string(),
  response: z.string(),
});

export const teacherTipsSchema = z.object({
  teacherTip: z.string(),
});

export const lessonEquipmentAndResourcesSchema = z.object({
  equipment: z.string(),
});

export const keyLearningPointsSchema = z.object({
  keyLearningPoint: z.string().nullable(),
});

export type keyLearningPoint = z.infer<typeof keyLearningPointsSchema>;

export const legacyCopyrightContentSchema = z
  .array(
    z.object({
      copyrightInfo: z.string(),
    }),
  )
  .nullable()
  .optional();

export type LegacyCopyrightContent = z.infer<
  typeof legacyCopyrightContentSchema
>;

export const keywordsSchema = z.object({
  keyword: z.string(),
  description: z.string(),
});

const stemTextObjectSchema = z.object({
  text: z.string(),
  type: z.literal("text"),
});

export type StemTextObject = z.infer<typeof stemTextObjectSchema>;

export const isStemTextObject = (
  obj: StemTextObject | StemImageObject,
): obj is StemTextObject => {
  return obj.type === "text";
};

const stemImageObjectSchema = z.object({
  imageObject: z.object({
    format: z.enum(["png", "jpg", "jpeg", "webp", "gif", "svg"]).optional(),
    secureUrl: z.url(),
    url: z.url().optional(),
    height: z.number().optional(),
    width: z.number().optional(),
    metadata: z.union([
      z.array(z.any()),
      z.object({
        attribution: z.string().optional(),
        usageRestriction: z.string().optional(),
      }),
    ]),
    publicId: z.string().optional(),
    version: z.number().optional(),
  }),
  type: z.literal("image"),
});

export type StemImageObject = z.infer<typeof stemImageObjectSchema>;

export type StemObject = StemTextObject | StemImageObject;

const mcAnswer = z.object({
  answer: z
    .array(z.union([stemTextObjectSchema, stemImageObjectSchema]))
    .min(1),
  answerIsCorrect: z.boolean(),
});

export type MCAnswer = z.infer<typeof mcAnswer>;

const matchAnswer = z.object({
  correctChoice: z.array(stemTextObjectSchema).length(1),
  matchOption: z.array(stemTextObjectSchema).length(1),
});

export type MatchAnswer = z.infer<typeof matchAnswer>;

const orderAnswer = z.object({
  answer: z.array(stemTextObjectSchema).length(1),
  correctOrder: z.number(),
});

export type OrderAnswer = z.infer<typeof orderAnswer>;

const shortAnswer = z.object({
  answer: z.array(stemTextObjectSchema).length(1),
  answerIsDefault: z.boolean(),
});

export type ShortAnswer = z.infer<typeof shortAnswer>;

const answersSchema = z.object({
  "multiple-choice": z.array(mcAnswer).nullable().optional(),
  match: z.array(matchAnswer).nullable().optional(),
  order: z.array(orderAnswer).nullable().optional(),
  "short-answer": z.array(shortAnswer).nullable().optional(),
  "explanatory-text": z.null().optional(),
});

export type AnswersSchema = z.infer<typeof answersSchema>;

export const lessonPathwaySchema = z.object({
  programmeSlug: z.string(),
  unitSlug: z.string(),
  unitTitle: z.string(),
  keyStageSlug: z.string(),
  keyStageTitle: z.string(),
  subjectSlug: z.string(),
  subjectTitle: z.string(),
  phaseTitle: z.string().nullish(),
  lessonCohort: z.string().nullish(),
  examBoardSlug: z.string().nullish(),
  examBoardTitle: z.string().nullish(),
  tierSlug: z.string().nullish(),
  tierTitle: z.string().nullish(),
  yearGroupSlug: z.string().nullish(),
  yearGroupTitle: z.string().nullish(),
  pathwayTitle: z.string().nullish(),
});

export type LessonPathway = z.infer<typeof lessonPathwaySchema>;

export const lessonOverviewQuizQuestionSchema = z.object({
  questionId: z.number(),
  questionUid: z.string(),
  questionType: z.enum([
    "multiple-choice",
    "match",
    "order",
    "short-answer",
    "explanatory-text",
  ]),
  questionStem: z
    .array(z.union([stemTextObjectSchema, stemImageObjectSchema]))
    .min(1),
  answers: answersSchema.nullable().optional(),
  feedback: z.string(),
  hint: z.string(),
  active: z.boolean(),
});

export type LessonOverviewQuizQuestion = z.infer<
  typeof lessonOverviewQuizQuestionSchema
>;

export const lessonOverviewQuizData = z
  .array(lessonOverviewQuizQuestionSchema)
  .nullable()
  .optional();

export type LessonOverviewQuizData = z.infer<typeof lessonOverviewQuizData>;

const actionsSchemaCamel = zodToCamelCase(actionsSchema, {
  bidirectional: true,
});
export const baseLessonOverviewSchema = z.object({
  isLegacy: z.boolean(),
  lessonSlug: z.string(),
  lessonTitle: z.string(),
  tierTitle: tierDescriptions.nullable(),
  tierSlug: tierSlugs.nullable(),
  pathwayTitle: pathwayDescriptions.nullable(),
  contentGuidance: z
    .array(contentGuidanceSchemaCamelCase)
    .nullable()
    .optional(),
  misconceptionsAndCommonMistakes: z
    .array(misconceptionsAndCommonMistakesSchema)
    .nullable()
    .optional(),
  teacherTips: z.array(teacherTipsSchema).nullable().optional(),
  lessonEquipmentAndResources: z
    .array(lessonEquipmentAndResourcesSchema)
    .nullable()
    .optional(),
  additionalMaterialUrl: z.string().nullable().optional(),
  keyLearningPoints: z.array(keyLearningPointsSchema).nullable().optional(),
  pupilLessonOutcome: z.string().nullable().optional(),
  lessonKeywords: z.array(keywordsSchema).nullable().optional(),
  legacyCopyrightContent: legacyCopyrightContentSchema,
  supervisionLevel: z.string().nullable(),
  worksheetUrl: z.string().nullable(),
  presentationUrl: z.string().nullable(),
  videoMuxPlaybackId: z.string().nullable(),
  videoWithSignLanguageMuxPlaybackId: z.string().nullable(),
  transcriptSentences: z.union([z.array(z.string()), z.string()]).nullable(),
  isWorksheetLandscape: z.boolean().optional().nullable(),
  hasLegacyCopyrightMaterial: z.boolean().optional().nullable(),
  expired: z.boolean().nullable(),
  starterQuiz: lessonOverviewQuizData,
  exitQuiz: lessonOverviewQuizData,
  videoTitle: z.string().nullish(),
  lessonCohort: z.string().nullish(),
  updatedAt: z.string(),
  lessonGuideUrl: z.string().nullable(),
  phonicsOutcome: z.string().nullish(),
  actions: actionsSchemaCamel.nullish(),
  hasMediaClips: z.boolean(),
  additionalFiles: z.array(z.string()).nullable(),
  lessonMediaClips: mediaClipsRecordCamelSchema.nullish(),
  lessonOutline: z.array(z.object({ lessonOutline: z.string() })).nullable(),
  lessonReleaseDate: z.string().nullable(),
  orderInUnit: z.number().nullable(),
  unitTotalLessonCount: z.number().nullable(),
  loginRequired: z.boolean(),
  geoRestricted: z.boolean(),
  excludedFromTeachingMaterials: z.boolean(),
});
export type LessonBase = z.infer<typeof baseLessonOverviewSchema>;

export const lessonUnitDataByKsSchema =
  syntheticUnitvariantsWithLessonIdsByKsSchema.pick({
    lesson_count: true,
    supplementary_data: true,
  });

export type LessonUnitDataByKs = Pick<
  SyntheticUnitvariantsWithLessonIdsByKsCamel,
  "lessonCount" | "supplementaryData"
>;

export const lessonDownloadsListSchema = z.array(
  z.object({
    exists: z.boolean().nullable(),
    type:
      z.enum([
        "presentation",
        "intro-quiz-questions",
        "intro-quiz-answers",
        "exit-quiz-questions",
        "exit-quiz-answers",
        "worksheet-pdf",
        "worksheet-pptx",
        "supplementary-pdf",
        "supplementary-docx",
        "curriculum-pdf",
        "lesson-guide-pdf",
        "additional-files",
      ]) || z.string().regex(/^additional-file-\d+$/),
    label: z.string(),
    ext: z.string(),
    forbidden: z.union([
      z.array(z.object({ copyright_info: z.string() })),
      z.boolean().optional().nullish(),
    ]),
    assetId: z.number().optional(),
    size: z.number().optional(),
  }),
);

export const lessonAdditionalFilesListSchema = z.array(
  z.object({
    exists: z.boolean().nullable(),
    type: z.enum(["additional-files"]),
    label: z.string(),
    ext: z.string(),
    forbidden: z.union([
      z.array(z.object({ copyright_info: z.string() })),
      z.boolean().optional().nullish(),
    ]),
    assetId: z.number(),
    size: z.number(),
  }),
);

export const baseLessonDownloadsSchema = z.object({
  isLegacy: z.boolean(),
  lessonSlug: z.string(),
  lessonTitle: z.string(),
  phaseSlug: phaseSlugs,
  subjectParent: z.string().nullable(),
  pathwaySlug: pathwaySlugs.nullable(),
  downloads: lessonDownloadsListSchema,
  additionalFiles: lessonAdditionalFilesListSchema,
  expired: z.boolean().nullable(),
  legacyCopyrightContent: legacyCopyrightContentSchema,
  updatedAt: z.string(),
  geoRestricted: z.boolean().nullable(),
  loginRequired: z.boolean().nullable(),
  actions: actionsSchemaCamel.nullish(),
  lessonReleaseDate: z.string().nullable(),
  year: years,
});

export const lessonListItemSchema = z.object({
  lessonSlug: z.string(),
  lessonTitle: z.string(),
  description: z.string(),
  pupilLessonOutcome: z.string().nullish(),
  expired: z.boolean().nullable(),
  quizCount: z.number().nullish(),
  videoCount: z.number().nullish(),
  presentationCount: z.number().nullish(),
  worksheetCount: z.number().nullish(),
  hasLegacyCopyrightMaterial: z.boolean().nullish(),
  orderInUnit: z.number().nullish(),
  lessonCohort: z.string().nullish(),
  actions: actionsSchemaCamel.nullish(),
  isUnpublished: z.literal(false),
  lessonReleaseDate: z.string().nullable(),
  geoRestricted: z.boolean(),
  loginRequired: z.boolean(),
});

export type LessonListItem = z.infer<typeof lessonListItemSchema>;

export const unpublishedLessonListItemSchema = z.object({
  lessonSlug: z.string(),
  lessonTitle: z.string(),
  orderInUnit: z.number().nullish(),
  isUnpublished: z.literal(true),
  lessonReleaseDate: z.string().nullable(),
  expired: z.boolean().nullable(),
});

export type UnpublishedLessonListItem = z.infer<
  typeof unpublishedLessonListItemSchema
>;
export const lessonListSchema = z.array(
  z.union([lessonListItemSchema, unpublishedLessonListItemSchema]),
);

export type LessonListSchema = z.infer<typeof lessonListSchema>;

export const legacyAssetObjectSchema = z
  .object({
    google_drive: z.object({
      id: z.string(),
      url: z.string(),
    }),
    google_drive_downloadable_version: z
      .object({
        id: z.string(),
        url: z.string().nullish(),
      })
      .nullish(),
  })
  .nullish();

export const lessonShareResourceTypeSchema = z.enum([
  "starter-quiz",
  "exit-quiz",
  "video",
]);
export const lessonShareResourceSchema = z.object({
  exists: z.boolean().nullable(),
  type: lessonShareResourceTypeSchema,
  label: z.string(),
  metadata: z.string().nullable(),
});

export const redirectSchema = z.object({
  incoming_path: z.string(),
  outgoing_path: z.string(),
  redirect_type: z.union([
    z.literal(301),
    z.literal(302),
    z.literal(303),
    z.literal(307),
    z.literal(308),
  ]),
});

// Type representing the raw data from the API (snake_case)
export type RedirectData = {
  incoming_path: string;
  outgoing_path: string;
  redirect_type?: 301 | 302 | 303 | 307 | 308;
};

// Type representing the processed data in camelCase for use in the application
export type Redirect = {
  incomingPath: string;
  outgoingPath: string;
  redirectType: 301 | 302 | 303 | 307 | 308;
};

export type KeystageSlug = z.infer<typeof keystageSlugs>;
